import {btn, comp, Component, Config, DateTime, EntityID, menu, Notifier, p, router, tbar, win,} from "@intermesh/goui";
import {ServerApi} from "./util/ServerApi.js";
import {CurrentSection} from "./services/CurrentSection.js";
import {CurrentCourse} from "./services/CurrentCourse.js";
import {CurrentParticipant} from "./services/CurrentParticipant.js";
import {ScormApi} from "./util/ScormApi.js";
import {t} from "./util/Translate.js";
import {Attempt, ParticipantEntity} from "./data/ParticipantsStore.js";
import {CourseEntity} from "./data/CoursesStore.js";
import {BlockEntity} from "./data/BlocksStore.js";
import {ifwLog} from "./util/IfwLog.js";
import {registorWindow} from "./components/ErrorLogger.js";
import {CurrentUser} from "./services/CurrentUser.js";

declare global {
	var API: ScormApi;
	var finishScorm: any;
}

export class Course extends Component {

	private currentSection;
	private currentCourse: CourseEntity | undefined;
	private currentParticipant: ParticipantEntity;

	private scormUrl: string | undefined;

	private courseBlock: BlockEntity | undefined;

	private currentAttempt: Partial<Attempt> | undefined;

	private language: string | undefined;
	private alternativeLanguage: string | undefined;
	private iframe: Component | undefined;
	private block: any;

	private showFrame: boolean | undefined;

	constructor(courseId?: string, blockId?: string) {
		super("section");
		this.itemId = "course";
		this.id = "course";
		this.cls = "vbox";

		this.currentSection = CurrentSection.get();
		this.currentParticipant = CurrentParticipant.get();

		this.language = sessionStorage.getItem("language")!;

		window.API = new ScormApi();

		window.API.LMSFinish = function (this: ScormApi, _param: any) {
			window.finishScorm(this.data["cmi.core.score.raw"], this.data["cmi.core.lesson_status"] === "passed");
			return "true";
		};

		window.finishScorm = (score: number, success: boolean | string) => {

			//hide frame immediately
			this.showFrame = false;

			// see https://community.articulate.com/discussions/articulate-storyline/can-you-pass-sl-results-variables-on-to-javascript
			this.finishScorm(score, success === "true" || success === true);

			const pc = JSON.parse(localStorage.getItem("portalConfig")!);
			if (pc && pc.mayUseEva) {
				let ep = sessionStorage.getItem("evaPortal");
				if (ep) {
					CurrentUser.enableIdleLogout();
				}
			}
		};
		this.on("render", async () => {
			if (courseId) {
				this.currentCourse = await CurrentCourse.readIf(courseId);
			}
			await this.startNextBlock(blockId);
		});
	}

	async startNextBlock(blockId?: string | EntityID) {
		if (!blockId) {
			this.courseBlock = this.findNextBlock();
		} else {
			this.courseBlock = this.findBlockById(blockId);
		}

		if (!this.courseBlock) {
			// router.goto("course-finished"); // This route has not existed for more than six years.
			router.goto("profile"); // Not sure what to do here...
			return;
		}
		//check language

		let langBlock = this.findBlockLanguage(this.courseBlock, this.language!);

		if (!langBlock) {
			if (!this.alternativeLanguage) {
				await this.courseBlockLanguagePrompt(blockId);
				return;
			}
			langBlock = this.findBlockLanguage(this.courseBlock, this.alternativeLanguage!);
		}
		if (!langBlock) {
			langBlock = this.findBlockLanguage(this.courseBlock, "NL");
		}

		this.block = langBlock;

		this.currentAttempt = {
			courseId: this.currentCourse!.id,
			participantId: this.currentParticipant!.id,
			blockId: langBlock.blockId,
			sectionId: this.currentSection!.id
		} as Partial<Attempt>;
		this.currentParticipant!.attempts!.push(<Attempt>this.currentAttempt);

		CurrentParticipant.save({attempts: this.currentParticipant!.attempts}).then((result) => {
			this.currentParticipant = result.data;
			this.currentAttempt = this.currentParticipant!.attempts![this.currentParticipant!.attempts!.length - 1]!;

			this.scormUrl = ServerApi.scormUrl(langBlock.blockId, langBlock.languageId);

			let iframeParams = {
				id: 'scormFrame',
				tagName: 'iframe',
				attr: {
					src: this.scormUrl,
				},
				cls: "scorm-frame"
			} as Config<any>;
			this.showFrame = true;
			if (langBlock.content.fullScreen) {
				this.showFrame = false;
				iframeParams.cls += " full-screen";
			}
			this.items.replace(this.iframe = comp(iframeParams));

			const pc = JSON.parse(localStorage.getItem("portalConfig")!);
			if (pc && pc.mayUseEva) {
				let ep = sessionStorage.getItem("evaPortal");
				if (ep) {
					CurrentUser.disableIdleLogout();
				}
			}

			registorWindow((this.iframe.el as HTMLIFrameElement).contentWindow!);
		});
	}

	findBlockById(id: string | EntityID) {
		return this.currentCourse!.blocks.find((b: BlockEntity) => {
			return String(b.blockId) === id;
		}) || undefined;
	}


	findNextBlock() {
		for (const b of this.currentCourse!.blocks) {
			if (!this.hasCompletedBlock(b.blockId)) {
				return b;
			}
		}
		return undefined;
	}

	findBlockLanguage(courseBlock: BlockEntity, language: string) {
		for (const l of courseBlock.block.languages) {
			if (l.language.key === language) {
				return l;
			}
		}
		return false;
	}

	finishScorm(score: number, success: boolean) {
		if (!score && success) {
			score = 100;
		}
		this.iframe!.hide();
		/*
		if (!this.hasCompletedBlock(this.block.blockId)) {
			// Setting this item will automatically send certificate when we finish for the first time.
			// This is picked uup in the course status screen
			sessionStorage.setItem("justFinishedCourse", "1");
		}
		 */
		/*
		 * Setting this session variable will automatically send certificate when we finish. There are two scenarios:
		 * 1. The block has just been finished for the first time by the participant
		 * 2. The option autoSendCertificateRepeatCourse is explicitly enabled for the current section In this case, the
		 * last attempt is leading!
		 *
		 * This session variable is picked up in the course status screen
         */
		if (!this.currentSection.autoSendCertificateRepeatCourse && !this.hasCompletedBlock(this.block.blockId)) {
			sessionStorage.setItem("justFinishedCourse", "1");
		} else if (this.currentSection.autoSendCertificateRepeatCourse && success) {
			sessionStorage.setItem("justFinishedCourse", "1");
		}

		// Check whether we need to refresh this.currentAttempt
		this.currentAttempt!.succeeded = success;
		this.currentAttempt!.score = Math.round(score);
		this.currentAttempt!.finishedAt = (new DateTime()).format("c");
		delete this.currentAttempt!.remindAt; // This will force the remind date to be set in the API

		const promise = CurrentParticipant.save({attempts: this.currentParticipant!.attempts});
		promise.then(async (response) => {
			await CurrentParticipant.refresh(response);
		}).catch(function (response) {
			Notifier.error(t("Error while saving the progress. A log entry has been sent."));
			console.error(response);
			ifwLog("GouiFrontend", response);
		}).finally(() => {
			router.goto("course-status")
		});
	}

	/**
	 * Check whether there is an eaorlier attempt for this block that is (A) successful and (B) not expired
	 *
	 * In certain cases, only the last attempt is relevant, in that case, only look at the last attempt
	 *
	 * @param number blockId
	 * @param ?booleean lastAttemptIsLeading
	 * @return boolean
	 */
	hasCompletedBlock(blockId: number, lastAttemptIsLeading = false): boolean {
		const cp = this.currentParticipant;
		if (!cp.attempts) {
			return false;
		}
		let hasCompleted = false;
		for (const a of cp.attempts) {
			if (parseInt(a.blockId) === blockId) {
				// Github 832: The _final_ attempt is leading, so we finish the loop entirely.
				hasCompleted = (a.succeeded && new Date(a.expiresAt) > new Date());
				if (hasCompleted && !lastAttemptIsLeading) {
					break;
				}
			}
		}

		return hasCompleted;
	}

	private async courseBlockLanguagePrompt(blockId?: string | EntityID) {
		const c = comp({cls: "vbox pad scroll"}, p({html: t("The next module is not available in your language. Please choose one of the available languages to continue:")})),
			w = win({
					title: t("Select your language"),
					closable: false,
					maximizable: false,
					collapsible: false,
					draggable: false,
					resizable: false,
					modal: true
				},
			);
		let languageBtns = [];
		for (const l of this.courseBlock!.block.languages) {
			const lkey = l.language.key;
			const imgSrc = "resources/flags/" + lkey.toLowerCase() + ".svg";
			languageBtns.push(btn({
				cls: "ifw-language-btn",
				flex: 1,
				html: `<img src="${imgSrc}" alt="${t("lang" + lkey)}" class="go-avatar small">${t("lang" + lkey)}`,
				handler: () => {
					this.alternativeLanguage = lkey;
					w.close();
					this.startNextBlock(blockId);
				}
			}));
		}
		c.items.add(tbar({}, menu({cls: "ifw-language-select"}, ...languageBtns)));
		w.items.add(c);
		w.show();
	}
}